import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Car, ArrowLeft, MapPinned, Ban, Navigation2, Building2 } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import { SeoFooter } from "@/components/SeoFooter";
import { CityRating } from "@/components/CityRating";
import { FaqSection } from "@/components/FaqSection";
import { useSeo, useJsonLd, ogImageUrl } from "@/lib/seo";
import { CITIES, CITY_PATH, findCity } from "@/lib/cities";
import { cityFaq } from "@/lib/faq";
import { cityExtras } from "@/lib/cityExtras";
import { THEMATIC_PAGES } from "@/lib/thematic";
import { findDepartmentByCity, DEPT_PATH } from "@/lib/departments";
import { tripsFromCity, tripSlug, TRIP_PATH } from "@/lib/trips";
import { cityCoords, CHARGING_PATH, nearbyCities } from "@/lib/cityCoords";
import { MODELS, MODEL_PATH } from "@/lib/models";

export default function CityPage() {
  const { slug } = useParams();
  const city = findCity(slug);
  const extras = city ? cityExtras(city.slug) : null;
  const faqItems = city ? [...cityFaq(city), ...((extras && extras.extraFaq) || [])] : [];

  // Hooks must run unconditionally (before any early return).
  useSeo(
    city ? `GPS voiture sans permis ${city.prep} — itinéraires 45 km/h | vspo` : "vspo",
    city
      ? `Calculez un itinéraire pour voiture sans permis (VSP) ${city.prep} avec vspo : trajets adaptés à 45 km/h qui évitent les autoroutes et voies rapides comme ${city.road}, navigation vocale et trafic en temps réel.`
      : "",
    city ? `${CITY_PATH}/${city.slug}` : undefined,
    city ? ogImageUrl({ city: city.name }) : undefined
  );

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  useJsonLd(
    "city-jsonld",
    city
      ? [
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Accueil", item: `${origin}/` },
              { "@type": "ListItem", position: 2, name: `GPS VSP ${city.prep}`, item: `${origin}${CITY_PATH}/${city.slug}` },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "WebApplication",
            name: `vspo — GPS voiture sans permis ${city.prep}`,
            url: `${origin}${CITY_PATH}/${city.slug}`,
            applicationCategory: "TravelApplication",
            operatingSystem: "Web",
            inLanguage: "fr-FR",
            offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
            areaServed: { "@type": "City", name: city.name },
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqItems.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ]
      : null
  );

  // Unknown city -> redirect to the app (avoids indexing empty pages).
  if (!city) return <Navigate to="/" replace />;

  const others = CITIES.filter((c) => c.slug !== city.slug);
  const dept = findDepartmentByCity(city.slug);
  const trips = tripsFromCity(city.slug);
  const hasCharging = !!cityCoords(city.slug);
  const nearby = nearbyCities(city.slug, 6).map((s) => findCity(s)).filter(Boolean);

  return (
    <div className="min-h-screen w-full overflow-y-auto bg-background">
      <header className="sticky top-0 z-10 border-b border-border bg-card/95 backdrop-blur-md">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground">
              <Car className="h-5 w-5" />
            </span>
            <div className="leading-tight">
              <div className="font-display text-lg font-bold text-foreground">vspo</div>
              <div className="text-[11px] text-muted-foreground">GPS voiture sans permis · 45 km/h</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <ThemeToggle />
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground"
              data-testid="city-open-app"
            >
              <ArrowLeft className="h-4 w-4" /> Ouvrir le GPS
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 pb-20 pt-6">
        <nav className="mb-3 text-xs text-muted-foreground" aria-label="Fil d'ariane">
          <Link to="/" className="hover:text-primary">Accueil</Link> ·{" "}
          <Link to="/a-propos" className="hover:text-primary">À propos</Link> ·{" "}
          <span className="text-foreground">{city.name}</span>
        </nav>

        <h1 className="font-display text-3xl font-bold text-foreground" data-testid="city-title">
          GPS voiture sans permis {city.prep}
        </h1>
        <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">
          Vous roulez en <strong>voiture sans permis {city.prep}</strong> ? <strong>vspo</strong> calcule
          des itinéraires réellement adaptés à votre voiturette (bridée à <strong>45 km/h</strong>) qui
          évitent automatiquement les autoroutes et les voies rapides — comme <strong>{city.road}</strong> —
          interdites aux VSP.
        </p>

        <CityRating />

        {extras && (
          <section className="mt-8" data-testid="city-extras">
            <p className="text-[15px] leading-relaxed text-muted-foreground">{extras.lead}</p>
            {extras.zones && extras.zones.length > 0 && (
              <div className="mt-4">
                <h2 className="font-display text-lg font-bold text-foreground">
                  Quartiers où rouler en VSP {city.prep}
                </h2>
                <div className="mt-2 flex flex-wrap gap-2">
                  {extras.zones.map((z) => (
                    <span
                      key={z}
                      className="rounded-full border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground"
                      data-testid="city-zone"
                    >
                      {z}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {extras.tips && extras.tips.length > 0 && (
              <div className="mt-4">
                <h2 className="font-display text-lg font-bold text-foreground">
                  Conseils pratiques {city.prep}
                </h2>
                <ul className="mt-2 ml-1 space-y-2 text-[15px] leading-relaxed text-muted-foreground">
                  {extras.tips.map((t, i) => (
                    <li key={t || i}>• {t}</li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        )}

        <section className="mt-8">
          <div className="mb-3 flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Ban className="h-5 w-5" />
            </span>
            <h2 className="font-display text-xl font-bold text-foreground">Voies interdites {city.prep}</h2>
          </div>
          <p className="text-[15px] leading-relaxed text-muted-foreground">
            {city.prep.charAt(0).toUpperCase() + city.prep.slice(1)}, les VSP ne peuvent pas emprunter
            {" "}<strong>{city.road}</strong> ni les autres voies rapides et voies pour automobiles.
            vspo connaît ces axes et trace un trajet qui passe par le réseau ordinaire, en respectant la
            signalisation locale.
          </p>
        </section>

        <section className="mt-8">
          <div className="mb-3 flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <MapPinned className="h-5 w-5" />
            </span>
            <h2 className="font-display text-xl font-bold text-foreground">Un GPS pensé pour les VSP {city.prep}</h2>
          </div>
          <ul className="ml-1 space-y-2 text-[15px] leading-relaxed text-muted-foreground">
            <li>• Itinéraires 45 km/h évitant autoroutes et voies rapides</li>
            <li>• Navigation vocale en français et alertes d'approche</li>
            <li>• Trafic en temps réel, contournement des bouchons et accidents</li>
            <li>• Recherche d'adresses précises et lieux d'intérêt {city.prep}</li>
            <li>• Favoris et partage de vos trajets</li>
          </ul>
        </section>

        <div className="mt-10 rounded-2xl border border-border bg-card p-5">
          <h2 className="font-display text-lg font-bold text-foreground">
            Calculez votre itinéraire {city.prep}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Ouvrez vspo et entrez votre départ et votre arrivée pour un trajet adapté à votre voiture sans permis.
          </p>
          <Link
            to="/"
            className="mt-3 inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground"
            data-testid="city-cta"
          >
            <Navigation2 className="h-4 w-4" /> Ouvrir le GPS
          </Link>
        </div>

        {nearby.length > 0 && (
          <section className="mt-10" data-testid="city-nearby">
            <div className="mb-3 flex items-center gap-2">
              <MapPinned className="h-4 w-4 text-primary" />
              <h2 className="font-display text-base font-bold text-foreground">Villes proches {city.prep}</h2>
            </div>
            <div className="flex flex-wrap gap-2">
              {nearby.map((c) => (
                <Link
                  key={c.slug}
                  to={`${CITY_PATH}/${c.slug}`}
                  className="rounded-full border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-primary"
                  data-testid={`city-nearby-${c.slug}`}
                >
                  {c.name}
                </Link>
              ))}
            </div>
          </section>
        )}

        <section className="mt-10">
          <div className="mb-3 flex items-center gap-2">
            <Car className="h-4 w-4 text-primary" />
            <h2 className="font-display text-base font-bold text-foreground">Modèles de voiture sans permis</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {MODELS.map((m) => (
              <Link
                key={m.slug}
                to={`${MODEL_PATH}/${m.slug}`}
                className="rounded-full border border-border bg-card px-3 py-1.5 text-sm text-primary transition-colors hover:bg-muted"
                data-testid={`city-model-${m.slug}`}
              >
                {m.name}
              </Link>
            ))}
          </div>
        </section>

        <section className="mt-10">
          <div className="mb-3 flex items-center gap-2">
            <Building2 className="h-4 w-4 text-primary" />
            <h2 className="font-display text-base font-bold text-foreground">Autres villes</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {others.map((c) => (
              <Link
                key={c.slug}
                to={`${CITY_PATH}/${c.slug}`}
                className="rounded-full border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-primary"
                data-testid={`city-link-${c.slug}`}
              >
                {c.name}
              </Link>
            ))}
          </div>
        </section>

        <section className="mt-10">
          <h2 className="font-display text-base font-bold text-foreground">Guides pratiques</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {THEMATIC_PAGES.map((t) => (
              <Link
                key={t.slug}
                to={t.path}
                className="rounded-full border border-border bg-card px-3 py-1.5 text-sm text-primary transition-colors hover:bg-muted"
                data-testid={`city-thematic-${t.slug}`}
              >
                {t.kicker}
              </Link>
            ))}
            {dept && (
              <Link
                to={`${DEPT_PATH}/${dept.slug}`}
                className="rounded-full border border-border bg-card px-3 py-1.5 text-sm text-primary transition-colors hover:bg-muted"
                data-testid="city-department"
              >
                VSP {dept.prep}
              </Link>
            )}
            {hasCharging && (
              <Link
                to={`${CHARGING_PATH}/${city.slug}`}
                className="rounded-full border border-border bg-card px-3 py-1.5 text-sm text-primary transition-colors hover:bg-muted"
                data-testid="city-charging"
              >
                Recharge {city.prep}
              </Link>
            )}
          </div>
        </section>

        {trips.length > 0 && (
          <section className="mt-8">
            <h2 className="font-display text-base font-bold text-foreground">
              Itinéraires au départ ou à l'arrivée {city.prep}
            </h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {trips.map((t) => {
                const f = findCity(t.from);
                const d = findCity(t.to);
                if (!f || !d) return null;
                return (
                  <Link
                    key={tripSlug(t)}
                    to={`${TRIP_PATH}/${tripSlug(t)}`}
                    className="rounded-full border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-primary"
                    data-testid={`city-trip-${tripSlug(t)}`}
                  >
                    {f.name} → {d.name}
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        <FaqSection items={faqItems} />

        <p className="mt-8 text-xs text-muted-foreground">
          Informations fournies à titre indicatif. Vous restez responsable du respect du code de la
          route et de la signalisation.
        </p>
      </main>
      <SeoFooter />
    </div>
  );
}
